/* @flow */

import React, { Fragment, type StatelessFunctionalComponent } from "react";
import { Route } from "react-router-dom";
import "../../signups/src/fonts.scss";
import "../../signups/src/base.scss";
import styles from "./App.scss";
import Link from "../../signups/src/components/Link";
import NavigationBar from "../../signups/src/components/NavigationBar";
import Thanks from "./components/Thanks";
import Marquee from "../../signups/src/components/Marquee";
import LegalFooter from "../../signups/src/components/LegalFooter";
import { StoreProvider } from "./components/StoreProvider";
import Grid from "../../signups/src/components/Grid";
import Button from "../../signups/src/components/Button";
import { Half, Third } from "../../signups/src/components/GridItem";
import Form from "./components/SandboxSignup";
import { CONTACT_SALES_LINK } from "../../common/index";

type Config = {
  recaptchaSiteKey: string
};

type AppProps = {
  config: Config,
  countries: Array<string>,
  countryCode: string
};

const App: StatelessFunctionalComponent<AppProps> = ({
  config,
  countries,
  countryCode
}) => (
  <StoreProvider countryCode={countryCode} countries={countries}>
    <Fragment>
      <NavigationBar />
      <Route
        path="/signup/sandbox"
        exact={true}
        component={props => (
          <Fragment>
            <Marquee
              title={<Fragment>Test Everything Braintree</Fragment>}
              subTitle={null}
              darkStyle={true}
              description={
                <div>
                  <p>
                    Entering our sandbox allows you to get a feel for the
                    Braintree experience before applying for a merchant account
                    or going to production.
                  </p>
                  <p>
                    Already in the sandbox?{" "}
                    <Link
                      href="https://sandbox.braintreegateway.com/login"
                      intent="simple"
                      target="_blank">
                      Sign in
                    </Link>
                    .
                  </p>
                </div>
              }
            />
            <main>
              <Form
                {...props}
                config={config}
                countries={countries}
                countryCode={countryCode}
              />
              <div className={styles["additional-information"]}>
                <Grid>
                  <Third>
                    <h3>Stay secure</h3>
                    <p>
                      The sandbox offers you a space to test payment processing
                      without risking your actual credit card details.
                    </p>
                  </Third>

                  <Third>
                    <h3>Access everything</h3>
                    <p>
                      Explore the full control panel as any merchant would see
                      it—including reporting tools and features like recurring
                      billing.
                    </p>
                  </Third>

                  <Third>
                    <h3>Experiment freely</h3>
                    <p>
                      With sandbox credentials, you can run sample transactions,
                      play with subscriptions, and test the API fully.
                    </p>
                  </Third>
                </Grid>
              </div>

              <Grid>
                <Half>
                  <div className={styles["next-steps"]}>
                    <h2>Ready for production?</h2>
                    <p>
                      Sign up for a Braintree account to start processing
                      transactions on your live site.
                    </p>
                    <Button
                      href={CONTACT_SALES_LINK}
                      intent="primary"
                      target="_blank">
                      Go live
                    </Button>
                  </div>
                </Half>

                <Half>
                  <div className={styles["helpful-links"]}>
                    <h3>Helpful links</h3>
                    <ul>
                      <li>
                        <Link
                          href="https://articles.braintreepayments.com/get-started/try-it-out#sandbox-vs.-production"
                          intent="simple"
                          target="_blank">
                          Sandbox vs. production
                        </Link>
                      </li>

                      <li>
                        <Link
                          href="https://developers.braintreepayments.com/reference/general/testing"
                          intent="simple"
                          target="_blank">
                          Sandbox testing values
                        </Link>
                      </li>

                      <li>
                        <Link
                          href="https://articles.braintreepayments.com/get-started/currencies#testing-in-multiple-currencies"
                          intent="simple"
                          target="_blank">
                          Creating new merchant accounts
                        </Link>
                      </li>

                      <li>
                        <Link
                          href="https://articles.braintreepayments.com/get-started/try-it-out#switching-from-sandbox-to-production"
                          intent="simple"
                          target="_blank">
                          Switching from sandbox to production
                        </Link>
                      </li>
                    </ul>
                  </div>
                </Half>
              </Grid>
            </main>
          </Fragment>
        )}
      />

      <Route
        path="/signup/sandbox/thanks"
        exact={true}
        component={props => (
          <main>
            <Thanks {...props} />
          </main>
        )}
      />

      <LegalFooter countryCode={countryCode} />
    </Fragment>
  </StoreProvider>
);

export default App;
